import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import LoginPage from "./pages/LoginPage";
import HealthPage from "./pages/HealthPage";
import IngestionPage from "./pages/IngestionPage";
import SourcesPage from "./pages/SourcesPage";
import ErrorsPage from "./pages/ErrorsPage";
import CostPage from "./pages/CostPage";
import UsersPage from "./pages/UsersPage";
import FeedbackPage from "./pages/FeedbackPage";
import DocsPage from "./pages/DocsPage";
import ToolsPage from "./pages/ToolsPage";
import LLMPlaygroundPage from "./pages/LLMPlaygroundPage";
import AIAnalyticsPage from "./pages/AIAnalyticsPage";
import WebAnalyticsPage from "./pages/WebAnalyticsPage";
import AdvisorPage from "./pages/AdvisorPage";
import {
  PulseRegular, DeveloperBoardRegular, WarningRegular, MoneyRegular, PeopleRegular, CommentRegular,
  BookOpenRegular, WrenchRegular, BrainCircuitRegular, DataBarVerticalRegular, LightbulbRegular, SignOutRegular, GlobeRegular,
  RssRegular,
} from "@fluentui/react-icons";
import { fluentIcon } from "./lib/fluentIcon";

const Activity = fluentIcon(PulseRegular);
const Cpu = fluentIcon(DeveloperBoardRegular);
const AlertTriangle = fluentIcon(WarningRegular);
const DollarSign = fluentIcon(MoneyRegular);
const Users = fluentIcon(PeopleRegular);
const MessageSquare = fluentIcon(CommentRegular);
const BookOpen = fluentIcon(BookOpenRegular);
const Wrench = fluentIcon(WrenchRegular);
const BrainCircuit = fluentIcon(BrainCircuitRegular);
const BarChart3 = fluentIcon(DataBarVerticalRegular);
const Lightbulb = fluentIcon(LightbulbRegular);
const LogOut = fluentIcon(SignOutRegular);
const Globe = fluentIcon(GlobeRegular);
const Rss = fluentIcon(RssRegular);

function NavItem({ to, icon: Icon, label }: { to: string; icon: any; label: string }) {
  const loc = useLocation();
  const active = loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
        active ? "bg-brand-blue/15 text-brand-blue" : "text-slate-400 hover:bg-slate-900 hover:text-slate-200"
      }`}
    >
      <Icon size={18} />
      <span className="text-sm font-medium">{label}</span>
    </Link>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useAuth();
  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="w-64 border-r border-slate-900 p-4 flex flex-col">
        <div className="flex items-center gap-2 px-2 py-4 mb-4">
          <img src="/logo.png" alt="Tomitra" className="w-8 h-8 rounded-lg object-cover" />
          <div>
            <div className="text-sm font-semibold">Tomitra</div>
            <div className="text-xs text-slate-500">Admin</div>
          </div>
        </div>

        <nav className="flex flex-col gap-1 flex-1">
          <NavItem to="/" icon={Activity} label="System Health" />
          <NavItem to="/advisor" icon={Lightbulb} label="Advisor" />
          <NavItem to="/ingestion" icon={Cpu} label="Ingestion" />
          <NavItem to="/sources" icon={Rss} label="Sources" />
          <NavItem to="/errors" icon={AlertTriangle} label="Error Logs" />
          <NavItem to="/cost" icon={DollarSign} label="Cost & Resources" />

          <div className="text-xs text-slate-600 uppercase tracking-wider px-4 mt-4 mb-1">Phase 2</div>
          <NavItem to="/users" icon={Users} label="Users & Usage" />
          <NavItem to="/feedback" icon={MessageSquare} label="Feedback" />

          <div className="text-xs text-slate-600 uppercase tracking-wider px-4 mt-4 mb-1">AI</div>
          <NavItem to="/ai" icon={BarChart3} label="AI Analytics" />
          <NavItem to="/web" icon={Globe} label="Web Analytics" />
          <NavItem to="/llm" icon={BrainCircuit} label="LLM Playground" />

          <div className="text-xs text-slate-600 uppercase tracking-wider px-4 mt-4 mb-1">Phase 3</div>
          <NavItem to="/docs" icon={BookOpen} label="Documentation" />
          <NavItem to="/tools" icon={Wrench} label="Tools" />
        </nav>

        <div className="border-t border-slate-900 pt-4 mt-4">
          <div className="flex items-center gap-3 px-2 py-2">
            {user?.photoURL && <img src={user.photoURL} className="w-8 h-8 rounded-full" alt="" />}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{user?.displayName}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
            <button onClick={() => signOut()} className="p-2 text-slate-500 hover:text-slate-200" title="Sign out">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto p-8">{children}</div>
      </main>
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, loading, signOut } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8">
        <div className="text-lg font-semibold text-red-400">Access denied</div>
        <div className="text-sm text-slate-400 text-center max-w-md">
          Your email <span className="text-slate-200">{user.email}</span> is not on the admin allowlist.
          Ask an existing admin to add you.
        </div>
        <button
          onClick={() => signOut()}
          className="px-4 py-2 bg-slate-900 border border-slate-800 rounded-lg hover:bg-slate-800 text-sm"
        >
          Sign out
        </button>
      </div>
    );
  }
  return <>{children}</>;
}

function AuthedApp() {
  return (
    <RequireAuth>
      <Shell>
        <Routes>
          <Route path="/" element={<HealthPage />} />
          <Route path="/advisor" element={<AdvisorPage />} />
          <Route path="/ingestion" element={<IngestionPage />} />
          <Route path="/sources" element={<SourcesPage />} />
          <Route path="/errors" element={<ErrorsPage />} />
          <Route path="/cost" element={<CostPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/ai" element={<AIAnalyticsPage />} />
          <Route path="/web" element={<WebAnalyticsPage />} />
          <Route path="/llm" element={<LLMPlaygroundPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Shell>
    </RequireAuth>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/*" element={<AuthedApp />} />
      </Routes>
    </AuthProvider>
  );
}
